import React, { useEffect, useState } from 'react';

// Styles & Assets
import styles from './UserQR.module.css';
import qrCode from '../../../assets/icons/qr-code.svg';
import logos from '../../../assets/common/logos.json';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import useHttp from '../../../hooks/useHttp';

// Components
import Modal from '../../UI/Modal/Modal';
import GenerateQRDialog from '../../Helpers/GenerateQRDialog/GenerateQRDialog';

// Models
import { IQRData } from '../../../interfaces/UtilInterfaces';
import { UserData } from '../../../interfaces/UserInfo';
import ErrorSpinnerWrapper from '../../UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import { useTranslation } from 'react-i18next';
import { getFullName } from '../../../services/getPatientAtribute';
import { useToast } from '../../../hooks/useToast';

// Other
import moment from 'moment';

interface Props {
    user: UserData
}

const UserQR: React.FC<Props> = ({ user }) => {

    const [showQRModal, setShowQRModal] = useState(false);

    const { isLoading, error, sendRequest } = useHttp<IQRData>();

    const [userQR, setUserQR] = useState<IQRData | null>(null);

    const [qrGeneratedAt, setQrGeneratedAt] = useState<string>();

    const { t } = useTranslation();

    const toast = useToast();

    // Fetch initial QR
    const handleClickOpenQr = () => {
        setShowQRModal(true);
        sendRequest({
            url: getEnvironmentApiUrl() + 'app_user_login/user_auth_qr/' + user.id,
            method: 'GET'
        }, (data) => {
            setUserQR(data);
        });
    }

    // Regenerate QR
    const handleRegenerateQR = () => {
        if (user?.id == null) return;
        sendRequest({
            url: getEnvironmentApiUrl() + 'app_user_login/regenerate_user_qr/' + user.id,
            method: 'POST'
        }, (data) => {
            setUserQR(data); // update with the new QR
        });
    };
    
    // Update last update date when qrData changes
    useEffect(() => {
        if (userQR?.created_at) {
            setQrGeneratedAt(moment(userQR.created_at).format('DD-MM-YYYY HH:mm:ss'));
        }
    }, [userQR]);

    const handleGenerateQR = () => {

        import('jspdf').then(({ jsPDF }) => {

            const qrCode = document.getElementById('qr-code-patient') as HTMLCanvasElement;
            const doc = new jsPDF();

            doc.addImage(logos.logoBC, 'string', 14, 14, 50, 11);

            doc.setFont('helvetica', 'normal', 'bold');
            doc.setFontSize(19);
            doc.text(t('access_code_HomeConnect') as string, doc.internal.pageSize.getWidth() / 2, 50, { align: 'center' });

            if (qrCode) {
                doc.addImage(qrCode, 'HTMLCanvasElement', doc.internal.pageSize.getWidth() / 2 - 32, 65, 64, 64);
                doc.setFont('helvetica', 'normal', 400);
                doc.setFontSize(10);
                doc.text(`${t('created_on')}: ${qrGeneratedAt}`, doc.internal.pageSize.getWidth() / 2, 135, { align: 'center' });
            } else {
                doc.text(t('lost_QR') as string + ':', doc.internal.pageSize.getWidth() / 2, 90, { align: 'center' });
            }

            doc.setFont('helvetica', 'normal', 400);
            doc.setFontSize(12);
            doc.text(t('QR_user_identifier') as string + ':', doc.internal.pageSize.getWidth() / 2, 180, { align: 'center' });

            doc.setFont('helvetica', 'normal', 'bold');
            doc.setFontSize(19);
            doc.text(getFullName(user), doc.internal.pageSize.getWidth() / 2, 195, { align: 'center' });

            doc.setFont('helvetica', 'normal', 400);
            doc.setFontSize(12);
            doc.text(t('log_identify') as string, doc.internal.pageSize.getWidth() / 2, 215, { align: 'center' });

            doc.setFontSize(10);

            doc.text(t('event_of_incident') as string, doc.internal.pageSize.getWidth() / 2, 230, { align: 'center', maxWidth: doc.internal.pageSize.getWidth() - 40 });
            doc.text(`${t('phone')}: ${window.config.PHONE_QR_FILE ?? '-'}`, doc.internal.pageSize.getWidth() / 2, 240, { align: 'center' });
            doc.text(`${t('email')}: ${window.config.EMAIL_QR_FILE ?? '-'}`, doc.internal.pageSize.getWidth() / 2, 245, { align: 'center' });
            doc.text(t('outside_office') as string, doc.internal.pageSize.getWidth() / 2, 250, { align: 'center' });

            doc.addImage(logos.logoBC, 'string', doc.internal.pageSize.getWidth() / 2 - 20.5, doc.internal.pageSize.getHeight() - 25, 41, 9);

            window.open(doc.output('bloburl'));

        }).catch(() => toast.error(t('resource_not_load')));

    };

    return (
        <>
            <button className={styles.buttonQR} onClick={handleClickOpenQr}>
                <img src={qrCode} alt='codigo qr' />
            </button>
            <Modal show={showQRModal} setShowModal={setShowQRModal}>
                <ErrorSpinnerWrapper loading={isLoading} error={error}>
                    <GenerateQRDialog
                        qrCode={userQR?.qr_code}
                        handlePrintQR={handleGenerateQR}
                        setShowModal={setShowQRModal}
                        onRegenerate={handleRegenerateQR}
                        qrGeneratedAt={qrGeneratedAt}
                    />
                </ErrorSpinnerWrapper>
            </Modal>
        </>
    );
};

export default UserQR;